<template>
  <div>
    <VLoadingSpinner v-if="loading" />
    <div v-else>
      <div class="bg-white p-base border-1 border-solid border-border">
        <VLowEnvelopeAllowanceWarning ec="branding" />
        <VMessage
          type="warning"
          :fullwidth="true"
          v-if="!permissionBranding"
        >
          <template v-slot:icon>
            <ExclamationTriangleIcon />
          </template>
          <template v-slot:text>
            {{ $t('branding.permissionWarning') }}
          </template>
        </VMessage>
        <VPlanPermissionBlocker
          ec="branding"
          v-else-if="!featureBranding"
          :planSize="$t('companyPermissionWarning.smallPlan')"
          blockedFeature="companyPermissionWarning.branding"
        />
        <div v-else>
          <div class="block md:grid gap-x-lg grid-cols-page mb-4">
            <h3 class="mb-none col-span-full">{{ $t('branding.brandColor') }}</h3>
            <hr class="my-sm col-span-full" />
            <p class="col-span-1 mb-base">{{ $t('branding.colourExplainer') }}</p>
            <div>
              <b class="col-span-1 block">{{ $t('branding.brandColor') }}</b>
              <div v-bind:style="{ 'background-color': branding.colour ? branding.colour : '#0098CE' }" class="brand-colour-display my-sm">
                <span class="text-white font-semibold">{{ brandingColourSpan }}</span>
              </div>
              <VButton
                  class="branding-buttons"
                  type="outline-1"
                  @click.native="openBrandColourModal()"
                  href="#"
                  data-ec="branding_page"
                  data-et="open_brand_colour_modal">
                <span>{{ $t('functional.edit') }}</span>
              </VButton>
              <VButton
                  class="branding-buttons ml-2"
                  type="outline-1"
                  @click.native="resetBrandingColor()"
                  href="#"
                  data-ec="branding_page"
                  data-et="reset_brand_colour">
                <span>{{ $t('functional.reset') }}</span>
              </VButton>
            </div>
          </div>
          <div class="block md:grid gap-x-lg grid-cols-page mb-4">
            <h3 class="mb-none col-span-full">{{ $t('branding.logo') }}</h3>
            <hr class="my-sm col-span-full" />
            <i18n path="branding.logoCanBeSeenBy" class="col-span-1 mb-base" tag="p" >
              <template v-slot:company>
                {{ company.name }}
              </template>
            </i18n>
            <div class="col-span-1">
              <b class="block">{{ $t('branding.logo') }}</b>
              <div class="my-sm p-sm brand-logo-display" v-bind:style="{ 'background-color': branding.colour ? branding.colour : '#0098CE' }">
                <div class="w-full h-full logo-bg" v-bind:style="`background: url('${branding.logo}')`">
                </div>
              </div>
              <VButton
                  type="outline-1"
                  @click.native="openBrandLogoModal()"
                  href="#"
                  class="block branding-buttons"
                  data-ec="billing_page"
                  data-et="add_credits_button">
                <span>{{ $t('functional.upload') }}</span>
              </VButton>
              <VButton
                  class="branding-buttons ml-2"
                  type="outline-1"
                  @click.native="resetBrandingLogo()"
                  href="#"
                  data-ec="branding_page"
                  data-et="reset_brand_logo">
                <span>{{ $t('functional.reset') }}</span>
              </VButton>
            </div>
          </div>
          <div class="block md:grid gap-x-lg grid-cols-page mb-4">
            <h3 class="mb-none col-span-full">{{ $t('branding.emailTemplates') }}</h3>
            <hr class="my-sm col-span-full" />
            <div class="col-span-1 mb-base">
             <p class="mb-base">{{ $t('branding.updateEmailTemplatesExplainer') }}</p>
             <p>{{ $t('branding.advancedFeature') }}</p>
            </div>
            <div class="col-span-1">
              <b class="block">{{ $t('branding.customTemplates') }}</b>
              <EmailsView />
            </div>
          </div>
        </div>
        <BrandColourModal :show="showColourModal" :initial-colour="branding.colour" @close="showColourModal = false;" />
        <BrandLogoModal :show="showLogoModal" :branding="branding" @close="showLogoModal = false;" />
      </div>
    </div>
  </div>
</template>

<script>
import VPlanPermissionBlocker from '@/views/components/PlanPermissionBlocker.vue';
import VLowEnvelopeAllowanceWarning from '@/views/components/LowEnvelopeAllowanceWarning.vue';
import EmailsView from '@/views/Emails.vue';

import { mapGetters } from 'vuex';
import VButton from '@/components/deprecated/atoms/Button.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import BrandColourModal from '@/views/modals/branding/colour.vue';
import BrandLogoModal from '@/views/modals/branding/logo.vue';
import { ExclamationTriangleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'BrandingView',
  inject: {
    $validator: '$validator',
  },
  components: {
    VButton,
    VMessage,
    VLoadingSpinner,
    VLowEnvelopeAllowanceWarning,
    VPlanPermissionBlocker,
    EmailsView,
    BrandColourModal,
    BrandLogoModal,
    ExclamationTriangleIcon,
  },
  data() {
    return {
      loading: false,
      showColourModal: false,
      showLogoModal: false,
    };
  },
  created() {
    this.getBranding();
  },
  computed: {
    ...mapGetters({
      company: 'company/company',
      branding: 'branding/getBranding',
      userHasRole: 'roles/userHasRole',
      featureEnabled: 'featureFlags/featureEnabled',
    }),
    brandingColourSpan() {
      return this.branding.colour ? this.branding.colour : '#0098ce';
    },
    permissionBranding() {
      return this.userHasRole('super-admin');
    },
    featureBranding() {
      return this.$store.getters['plan/permissions'].branding;
    },
    uploaderMessageText() {
      return screen.width < 500 ? this.$t('functional.uploaderMessageMobile'): this.$t('functional.uploaderMessage'); // eslint-disable-line
    },
  },
  methods: {
    resetBrandingColor() {
     this.updateBranding({
       colour: null,
     }).then(() => {
       this.$bus.$emit('renderGlobalMessage', ({ message: this.$t('branding.brandAttributeUpdatedMessage', { attribute: 'branding colour' }), icon: 'check', type: 'success' }));
     });
    },
    resetBrandingLogo() {
      this.updateBranding({
        logo: null,
      }).then(() => {
        this.$bus.$emit('renderGlobalMessage', ({ message: this.$t('branding.brandAttributeUpdatedMessage', { attribute: 'logo' }), icon: 'check', type: 'success' }));
      });
    },
    getBranding() {
        if (this.featureBranding) {
          this.loading = true;
          this.$store.dispatch('branding/GET').then(() => { this.loading = false; });
        }
    },
    openBrandColourModal() {
      this.showColourModal = true;
    },
    openBrandLogoModal() {
      this.showLogoModal = true;
    },
    updateBranding(payload) {
      this.loading = true;
      return this.$store.dispatch('branding/UPDATE', payload).finally(() => {
        this.loading = false;
      });
    },
  },
};
</script>

<style scoped>
.branding-buttons {
  float: none;
  margin-bottom: 15px;
  display: inline-block;
}
</style>
